/* Iframe.tsx generated by @compiled/babel-plugin v0.36.1 */
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./Iframe.compiled.css";
import * as React from 'react';
import { ax, ix } from "@compiled/react/runtime";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Fragment, useRef, Suspense, useContext, useEffect, useMemo, useState, forwardRef } from 'react';
import { Box } from '@atlaskit/primitives/compiled';
import { CURRENT_SURFACE_CSS_VAR, useThemeObserver } from '@atlaskit/tokens';
import { EnvironmentContext } from '../../context';
import { useCustomUITunnelsList } from '../../web-client';
import { parseExtensionId, useMemoizedStateObject } from '../../utils';
import stringify from 'fast-json-stable-stringify';
import { TracingProvider } from '../../error-reporting';
import { createSrcFromExtension, getBrowserLocale, getBrowserTimezone, getExtensionEntryPoint, getResolverContext, getSrcUrl, getViewContext } from './utils';
import { InnerIframe } from './InnerIframe';
import { getLoadingComponent } from './utils/getLoadingComponent';
import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';
import { getExtensionType } from '../../utils/getExtensionType';
import { fg } from '@atlaskit/platform-feature-flags';
import { ContextChangeObserverWrapper } from './ContextChangeObserverWrapper';
var styles = {
  hostedResourcesIframeContainer: "_kqswh2mm",
  box: "_1bsb1osq _4t3i1osq"
};
var ReloadOnContextChangeWrapper = function ReloadOnContextChangeWrapper(_ref) {
  var context = _ref.context,
    children = _ref.children,
    getCurrentForgeReactMajorVersion = _ref.getCurrentForgeReactMajorVersion;
  // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
  if (getCurrentForgeReactMajorVersion && fg('forge-ui-macro-config-update-improvement')) {
    return /*#__PURE__*/React.createElement(ContextChangeObserverWrapper, {
      context: context,
      getCurrentForgeReactMajorVersion: getCurrentForgeReactMajorVersion
    }, children);
  }
  return /*#__PURE__*/React.createElement(Fragment, {
    key: stringify(context)
  }, children);
};
export var Iframe = /*#__PURE__*/forwardRef(function (_ref2, iframeRef) {
  var _extension$properties2;
  var accountId = _ref2.accountId,
    extension = _ref2.extension,
    contextIds = _ref2.contextIds,
    apolloClient = _ref2.apolloClient,
    components = _ref2.components,
    coreData = _ref2.coreData,
    extensionData = _ref2.extensionData,
    extensionViewData = _ref2.extensionViewData,
    loadingComponent = _ref2.loadingComponent,
    onLoad = _ref2.onLoad,
    _ref2$height = _ref2.height,
    height = _ref2$height === void 0 ? '100%' : _ref2$height,
    _ref2$width = _ref2.width,
    width = _ref2$width === void 0 ? '100%' : _ref2$width,
    bridge = _ref2.bridge,
    customBridgeMethods = _ref2.customBridgeMethods,
    _ref2$isResizable = _ref2.isResizable,
    isResizable = _ref2$isResizable === void 0 ? true : _ref2$isResizable,
    entryPoint = _ref2.entryPoint,
    extensionPayload = _ref2.extensionPayload,
    getContextToken = _ref2.getContextToken,
    timezone = _ref2.timezone,
    locale = _ref2.locale,
    _ref2$isHidden = _ref2.isHidden,
    isHidden = _ref2$isHidden === void 0 ? false : _ref2$isHidden,
    _ref2$isInModal = _ref2.isInModal,
    isInModal = _ref2$isInModal === void 0 ? false : _ref2$isInModal,
    setShouldShowRenderer = _ref2.setShouldShowRenderer,
    setIsLoading = _ref2.setIsLoading,
    getCurrentForgeReactMajorVersion = _ref2.getCurrentForgeReactMajorVersion,
    onError = _ref2.onError;
  var environment = useContext(EnvironmentContext);

  // ARKEN-856: useThemeObserver() is a bit noisy, it returns copies of the same theme which breaks useBridge() dependencies
  // We use useMemoizedStateObject() to properly memoize its output doing a deep-equality check
  var theme = useMemoizedStateObject(useThemeObserver());
  var iframeWrapperRef = useRef(null);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    surfaceColor = _useState2[0],
    setSurfaceColor = _useState2[1];
  var _useForgeUiAnalyticsE = useForgeUiAnalyticsEvent(),
    trackExtensionLoaded = _useForgeUiAnalyticsE.trackExtensionLoaded;
  useEffect(function () {
    if (!iframeWrapperRef.current) {
      return;
    }
    var nextSurfaceColor = getComputedStyle(iframeWrapperRef.current).getPropertyValue(CURRENT_SURFACE_CSS_VAR);
    setSurfaceColor(nextSurfaceColor);
  }, [theme]);
  var _parseExtensionId = parseExtensionId(extension.id),
    appId = _parseExtensionId.appId,
    moduleKey = _parseExtensionId.moduleKey;
  var environmentId = extension.environmentId,
    environmentType = extension.environmentType;
  var extensionEntryPoint = getExtensionEntryPoint(extension, entryPoint);
  var defaultSrc = useMemo(function () {
    return createSrcFromExtension(appId, extension, environment, extensionEntryPoint);
  }, [appId, extension, environment, extensionEntryPoint]);
  var _useCustomUITunnelsLi = useCustomUITunnelsList({
      client: apolloClient,
      appId: appId,
      environmentId: environmentId,
      environmentType: environmentType
    }),
    tunnels = _useCustomUITunnelsLi.tunnels,
    loading = _useCustomUITunnelsLi.loading;
  useEffect(function () {
    var _extension$properties;
    if ((extension === null || extension === void 0 || (_extension$properties = extension.properties) === null || _extension$properties === void 0 ? void 0 : _extension$properties.render) !== 'native') {
      var _extensionData$macro, _extensionData$macro2;
      var extensionType = getExtensionType(extension);
      trackExtensionLoaded({
        forgeEnvironment: extension === null || extension === void 0 ? void 0 : extension.environmentType,
        renderType: 'CustomUI',
        extensionType: extensionType,
        isConfiguring: (_extensionData$macro = extensionData.macro) === null || _extensionData$macro === void 0 ? void 0 : _extensionData$macro.isConfiguring,
        isInserting: (_extensionData$macro2 = extensionData.macro) === null || _extensionData$macro2 === void 0 ? void 0 : _extensionData$macro2.isInserting
      });
    }
    // Adding `extension` itself or `trackExtensionLoaded` will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extension === null || extension === void 0 ? void 0 : extension.environmentType, extension === null || extension === void 0 || (_extension$properties2 = extension.properties) === null || _extension$properties2 === void 0 ? void 0 : _extension$properties2.render, extension === null || extension === void 0 ? void 0 : extension.type]);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isThreeLOPromptVisible = _useState4[0],
    setIsThreeLOPromptVisible = _useState4[1];
  var coreDataInner = _objectSpread(_objectSpread({}, coreData), {}, {
    environmentId: environmentId,
    environmentType: environmentType,
    moduleKey: moduleKey,
    siteUrl: window.location.origin
  });
  var resizingEnabled = isResizable && !extension.properties.viewportSize;
  return /*#__PURE__*/React.createElement(Box, {
    ref: iframeWrapperRef,
    testId: "hosted-resources-iframe-container",
    xcss: styles.hostedResourcesIframeContainer,
    style: {
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      minHeight: isThreeLOPromptVisible || isHidden ? undefined : height,
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      height: resizingEnabled || isThreeLOPromptVisible || isHidden ? undefined : height,
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
      width: width
    }
  }, loading && /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: styles.box
  }, loading && !isHidden && getLoadingComponent(loadingComponent))), !loading && /*#__PURE__*/React.createElement(TracingProvider, null, /*#__PURE__*/React.createElement(ReloadOnContextChangeWrapper, {
    getCurrentForgeReactMajorVersion: getCurrentForgeReactMajorVersion,
    context: getViewContext({
      resolverContext: getResolverContext(coreDataInner, extensionData),
      accountId: accountId,
      extension: extension,
      timezone: timezone !== null && timezone !== void 0 ? timezone : getBrowserTimezone(),
      locale: locale !== null && locale !== void 0 ? locale : getBrowserLocale(),
      extensionViewData: extensionViewData
    })
  }, /*#__PURE__*/React.createElement(InnerIframe, {
    accountId: accountId,
    extension: extension,
    contextIds: contextIds,
    apolloClient: apolloClient,
    components: components,
    coreData: coreDataInner,
    extensionData: extensionData,
    extensionViewData: extensionViewData,
    loadingComponent: loadingComponent,
    onLoad: onLoad,
    height: height,
    width: width,
    src: getSrcUrl(tunnels, extensionEntryPoint, defaultSrc),
    bridge: bridge,
    customBridgeMethods: customBridgeMethods,
    isResizable: resizingEnabled,
    getContextToken: getContextToken,
    timezone: timezone,
    locale: locale,
    setIsThreeLOPromptVisible: setIsThreeLOPromptVisible,
    setShouldShowRenderer: setShouldShowRenderer,
    extensionPayload: extensionPayload,
    isHidden: isHidden,
    theme: theme,
    surfaceColor: surfaceColor,
    appId: appId,
    ref: iframeRef,
    isInModal: isInModal,
    setIsLoading: setIsLoading,
    onError: onError
  }))));
});